// utils/twicpics.ts
//
// Thin, named entry points over utils/imageCdn.ts. Nothing here knows the
// vendor; that lives in the adapter inside imageCdn.ts.

import {
    cdnImage,
    cdnPlaceholder,
    cdnSrcSet,
    cdnWidthSrcSet,
    type ImageTransforms,
    STOREFRONT_CARD_SIZES,
    STOREFRONT_HERO_SIZES,
    transformsFromStrings,
} from '@/utils/imageCdn';

/**
 * The CDN URL for a main-bucket media image (listing photos, agreement pages,
 * attachments), or the original when the CDN is off or the URL is not ours.
 * Accepts the vocabulary object, or the older string list for callers not
 * yet converted.
 */
export const toTwicPicsUrl = (
    url?: string | null,
    transforms: ImageTransforms | string[] = {},
): string | null => {
    if (!url) {
        return null;
    }

    return cdnImage(url, Array.isArray(transforms) ? transformsFromStrings(transforms) : transforms);
};

/**
 * eSign page renders are ~1190px-wide PNGs shown in an 816px column. Ask for
 * that width, with the original (never upscaled) as the 2x candidate, so a
 * phone downloads a WebP a fraction of the PNG's size.
 */
export const ESIGN_PAGE_WIDTH = 816;

export const esignPageSrc = (url?: string | null): string =>
    cdnImage(url, {resize: ESIGN_PAGE_WIDTH});

export const esignPageSrcSet = (url?: string | null): string | undefined =>
    cdnSrcSet(url, ESIGN_PAGE_WIDTH);

/** A page or attachment thumbnail for lists and side columns. */
export const documentThumb = (url?: string | null, width: number = 400): string =>
    cdnImage(url, {resize: width});

export interface ResponsiveImageSet {
    src: string;
    srcset?: string;
    sizes?: string;
    lazySrc?: string;
}

/**
 * Everything a fluid storefront `<v-img>` needs for one photo: a sensible
 * `src` for browsers that ignore srcset, width candidates with the slot's
 * `sizes`, and a blurred placeholder. Off the CDN it degrades to the origin
 * with the generic placeholder the caller supplies.
 */
export const listingImageSet = (
    url: string | null | undefined,
    slot: 'card' | 'hero' = 'card',
    fallbackPlaceholder: string = '/images/vehicle-placeholder.jpg',
): ResponsiveImageSet => {
    const source = url ?? '';

    return {
        src: cdnImage(source, {resize: slot === 'card' ? 800 : 1200}),
        srcset: cdnWidthSrcSet(source),
        sizes: cdnWidthSrcSet(source) ? (slot === 'card' ? STOREFRONT_CARD_SIZES : STOREFRONT_HERO_SIZES) : undefined,
        lazySrc: cdnPlaceholder(source) ?? fallbackPlaceholder,
    };
};
